import { SearchBox } from '@/src/components/SearchBox/SearchBox'
import { Menu, MenuButton, MenuList } from '@/src/components/ui/Menu'
import { ALL_LOCATIONS, useLocationsContext } from '@/src/contexts/LocationsContext'
import { mapToEnergyTypeDto } from '@/src/features/procurement-and-markets/details/types/adjustment.utils'
import { useLoadPlanningCurrentLocation } from '@/src/features/procurement-and-markets/load-planning/contexts/LoadPlanningCurrentLocationContext'
import {
  useLoadPlanningEarliestConsumption,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { appColors } from '@/src/theme/foundations/colors'
import { Box, Flex, Text } from '@chakra-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useMemo, useRef, useState } from 'react'
import { MdOutlineLocationOn } from 'react-icons/md'
import { RxCaretDown, RxCaretUp } from 'react-icons/rx'
import { LoadPlanningLocationMenuItem } from './LoadPlanningLocationMenuItem'

export const LoadPlanningLocationSelect = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const { currentRegion, allLocations: locations, companyPgId: companyId } = useLocationsContext()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { locationId } = useLoadPlanningCurrentLocation()

  const { data: earliestConsumptions } = useLoadPlanningEarliestConsumption({
    companyId: companyId!,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    year: strategyMetadata?.year,
  })
  const eligibleLocationIds = useMemo(() => {
    if (!earliestConsumptions) return undefined
    return new Set([...earliestConsumptions.map(i => Number(i.locationId)), ALL_LOCATIONS])
  }, [earliestConsumptions])

  const companyName = currentRegion?.name ?? ''

  const filteredLocations = useMemo(() => {
    const items = searchTerm ? locations.filter(loc => loc.name.toLowerCase().includes(searchTerm.toLowerCase())) : locations

    if (!eligibleLocationIds) return items

    const included = items.filter(loc => eligibleLocationIds.has(loc.locationPgId))
    const notIncluded = items.filter(loc => !eligibleLocationIds.has(loc.locationPgId))

    return [...included, ...notIncluded]
  }, [searchTerm, locations, eligibleLocationIds])
  const selectedLocation = useMemo(() => locations.find(loc => loc.locationPgId === locationId), [locations, locationId])

  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: filteredLocations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 5,
  })

  if (!locations) return null
  return (
    <Menu closeOnSelect placement='bottom-start' onClose={() => setSearchTerm('')}>
      {({ isOpen }) => (
        <>
          <MenuButton>
            <Flex alignItems='center' gap={1.5}>
              <MdOutlineLocationOn size={16} color={appColors.gray[500]} />
              <Text fontSize={14} color={appColors.gray[500]} fontWeight={500} lineHeight={1}>
                {companyName}
              </Text>
              <Text fontSize={14} color={appColors.primary} fontWeight={500} lineHeight={1}>
                {selectedLocation?.name ?? ''}
              </Text>
              {isOpen ? <RxCaretUp size={18} /> : <RxCaretDown size={18} />}
            </Flex>
          </MenuButton>

          <MenuList minW='300px' px={2} py={1.5}>
            <Box mb={1} py={1.5} onKeyDown={e => e.stopPropagation()}>
              <SearchBox
                value={searchTerm}
                onChange={setSearchTerm}
                id={1}
                focusId={1}
                inputProps={{
                  _focus: { borderColor: 'gray.200', boxShadow: 'none' },
                }}
              />
            </Box>

            <Box ref={parentRef} maxH='300px' overflowY='scroll' w={'400px'}>
              <Box h={`${rowVirtualizer.getTotalSize()}px`} position='relative' w='100%'>
                {rowVirtualizer.getVirtualItems().map(virtualItem => {
                  const location = filteredLocations[virtualItem.index]
                  return (
                    <LoadPlanningLocationMenuItem
                      key={location.locationPgId}
                      location={location}
                      virtualItem={virtualItem}
                      eligibleLocationIds={eligibleLocationIds}
                    />
                  )
                })}
              </Box>
            </Box>
          </MenuList>
        </>
      )}
    </Menu>
  )
}
